import type { JoinPlanView } from "../../server/membership-views";
import { Collapsible } from "../ui-kit/collapsible";
import "./plan-card.css";

type Intro = { price: string; term: string; after: string } | null;

/** How many perks a card lists before "Show more". */
const shownPerks = 3;

/** How many members the plan's tier has ("51 members"), when its card shows them. */
export function PlanMembers({ label }: { label: string }) {
  return <p className="plan-members">{label}</p>;
}

/**
 * A plan's price in the surface's own price style (`className`): the price and how it bills, or
 * the plan's price struck beside its intro price ("$1 first month"), then what it costs after.
 */
export function PlanPrice({
  className,
  price,
  cadence,
  intro,
}: {
  className: string;
  price: string;
  cadence: string;
  intro: Intro;
}) {
  if (!intro)
    return (
      <p className={className}>
        {price} <span>{cadence}</span>
      </p>
    );
  return (
    <>
      <p className={className}>
        <s className="plan-was">
          <span className="sr-only">Regular price </span>
          {price}
        </s>{" "}
        {intro.price} <span>{intro.term}</span>
      </p>
      <p className="plan-then">{intro.after}</p>
    </>
  );
}

/**
 * What a plan includes, in the surface's own list style (`className`): the first three, then
 * the rest behind "Show more", which keeps them in the same list.
 */
export function PlanPerks({
  perks,
  label,
  className,
}: {
  perks: string[];
  label: string;
  className: string;
}) {
  if (!perks.length) return null;
  const rest = perks.slice(shownPerks);
  return (
    <ul className={className} aria-label={label}>
      {perks.slice(0, shownPerks).map((perk, index) => (
        <li key={index}>{perk}</li>
      ))}
      {rest.length > 0 && (
        <li className="plan-perks-more">
          <Collapsible label={`Show ${rest.length} more`} openLabel="Show less" triggerAfter>
            <ul className="plan-perks-rest">
              {rest.map((perk, index) => (
                <li key={index}>{perk}</li>
              ))}
            </ul>
          </Collapsible>
        </li>
      )}
    </ul>
  );
}

/** "Save 10% if you pay annually": a link to the yearly plan when it can be joined. */
export function PlanSavings({ savings }: { savings: { text: string; href: string | null } }) {
  return savings.href ? (
    <a className="plan-savings" href={savings.href}>
      {savings.text}
    </a>
  ) : (
    <p className="plan-savings">{savings.text}</p>
  );
}

/**
 * One plan as Membership lists it, and sign-in invites people to join: its name and members,
 * price or intro price, description, mailed rewards and places left, what it includes, and
 * the way in, with what paying yearly saves under it.
 */
export function PlanCard({ plan }: { plan: JoinPlanView & { example?: boolean } }) {
  return (
    <article className="plan">
      <div className="plan-head">
        <h3>{plan.title}</h3>
        {plan.members && <PlanMembers label={plan.members} />}
        <PlanPrice
          className="plan-price price"
          price={plan.price}
          cadence={plan.cadence}
          intro={plan.intro}
        />
        {plan.description && <p className="plan-description">{plan.description}</p>}
        {(plan.mail || plan.remaining) && (
          <p className="plan-notes">
            {plan.mail && <span>{plan.mail}</span>}
            {plan.remaining && <span>{plan.remaining}</span>}
          </p>
        )}
      </div>
      <PlanPerks perks={plan.benefits} label={`${plan.title} includes`} className="benefits" />
      <div className="plan-action">
        {plan.action.kind === "full" ? (
          <p className="payment-pending">{plan.title} is full. Check back soon.</p>
        ) : plan.action.kind === "join" ? (
          <a className="button button-primary" href={plan.action.href}>
            {plan.action.label} <span aria-hidden="true">→</span>
          </a>
        ) : (
          <p className="payment-pending">
            {plan.example ? "Example plan · not for sale" : "Checkout is opening soon."}
          </p>
        )}
        {plan.savings && <PlanSavings savings={plan.savings} />}
      </div>
    </article>
  );
}
